
import HandleResponses from '@common/utils/handleResponses';
import axios from '@/axios';
import { createMasterIndent, getIncoTerms, updateGenericAction, getChildIndents, updateApproval,
  searchMasterIndent, fetchAttachments, getBolTypes, cancelMasterIndent, findMasterIndent,
  updateMasterIndent, deliverMasterIndent, fetchMasterCustomFields,
} from '@/api-factory/exim';

const mutations = {
  updateMasterIndentById(state, data) {
    const result = state.masterIndents;
    const index = state.masterIndents.findIndex(indent => indent.master_indent_no === data.masterIndentNo);
    if (index !== -1 && data.status) {
      result[index].status = data.status;
      state.masterIndents = result;
    }
  },
  setLoadingStatus(state, status) {
    state.isLoading = status;
  },
  setPage(state, pageNo) {
    state.filter.page = pageNo;
  },
  paginationCheck(state, status) {
    state.isPaginating = status;
  },
  setAll(state, data) {
    const result = state.filter.page > 1 ? state.masterIndents.concat(data) : data;
    state.masterIndents = result;
    if (data.length) {
      state.filter.page += 1;
      state.hasEmpty = false;
    } else {
      state.hasEmpty = true;
    }
  },
  setIncoterm(state, data) {
    state.incoTerms = data;
  },
  setChildLeg(state, data) {
    state.childLegs = data;
  },
  setChildLegLoadingStatus(state, data) {
    state.isChildLegLoading = data;
  },
  setIsOrderProcessing(state, data) {
    state.isOrderProcessing = data;
  },
  setBolTypes(state, data) {
    state.bolTypes = data;
  },
  setCancelLoading(state, data) {
    state.isCancelLoading = data;
  },
  setCancelState(state, { indentNo, data }) {
    const masterIndex = state.masterIndents.findIndex(indent => indent.master_indent_no === indentNo);
    if (masterIndex !== -1) {
      state.masterIndents[masterIndex].status = 5;
    }
    state.childLegs = data;
  },
  setDeliverState(state, { indentNo }) {
    const masterIndex = state.masterIndents.findIndex(indent => indent.master_indent_no === indentNo);
    if (masterIndex !== -1) {
      state.masterIndents[masterIndex].status = 9;
    }
  },

  updateMasterIndentFields(state, data) {
    const veselDate = data.vessel_date;
    const indentNumber = data.indent_number;
    const masterIndex = state.masterIndents.findIndex(indent =>
      indent.master_indent_no === indentNumber);
    if (masterIndex !== -1) {
      state.masterIndents[masterIndex].container_details.vessel_sailing_date = veselDate;
    }
  },
  // entire master indent update
  updateMasterIndentsData(state, data) {
    const indentNumber = data.master_indent_no;
    const masterIndex = state.masterIndents.findIndex(indent =>
      indent.master_indent_no === indentNumber);
    if (masterIndex !== -1) {
      Object.assign(state.masterIndents[masterIndex], data);
    }
    state.masterIndents = [...state.masterIndents];
  },
};

const state = {
  isLoading: false,
  isChildLegLoading: false,
  isPaginating: false,
  masterIndents: [],
  childLegs: [],
  incoTerms: [],
  bolTypes: [],
  filter: {
    depot_id: null,
    page: 1,
    query: null,
    type: 1,
    sub_type: null,
    order_number: null,
  },
  isOrderProcessing: false,
  isCancelLoading: false,
  hasEmpty: false,
};

const getters = {};

const actions = {
  async findMasterIndent({ commit }, { indentNo }) {
    try {
      commit('setIsOrderProcessing', true);
      const result = await findMasterIndent(indentNo);
      return result && result.data;
    } catch (error) {
      return HandleResponses.Status(error);
    } finally {
      commit('setIsOrderProcessing', false);
    }
  },
  async updateIndent({ commit }, { indentNo, data }) {
    try {
      commit('setIsOrderProcessing', true);
      const result = await updateMasterIndent(indentNo, data);
      return result && result.data;
    } catch (error) {
      return HandleResponses.Status(error);
    } finally {
      commit('setIsOrderProcessing', false);
    }
  },
  async createIndent({ commit }, data) {
    try {
      commit('setIsOrderProcessing', true);
      const result = await createMasterIndent(data);
      return result && result.data;
    } catch (error) {
      const err = HandleResponses.Status(error);
      if (err && err.error === 'Validation  error - request - ERR_ACCEPTANCE_SLA_NOT_FOUND') {
        return {
          error: 'ERR_ACCEPTANCE_SLA_NOT_FOUND',
        };
      }
      return err;
    } finally {
      commit('setIsOrderProcessing', false);
    }
  },
  async incoterm({ commit }, params) {
    try {
      const result = await getIncoTerms(params);
      if (result && result.data) {
        commit('setIncoterm', result.data);
      }
      return result.data;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },
  async listOfBolType({ commit }) {
    try {
      const result = await getBolTypes();
      if (result && result.data) {
        commit('setBolTypes', result.data);
      }
      return result.data;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },
  async genericAction({ commit }, { indentNo, values }) {
    try {
      const result = await updateGenericAction({ indentNo, values });
      if (result && result.data) {
        commit('setChildLeg', result.data);
        const indentNumber = (result.data[0] && result.data[0].master_indent_number) ? result.data[0].master_indent_number : null;
        const status = (result.data[0] && result.data[0].master_indent_status) ? result.data[0].master_indent_status : null;
        commit('updateMasterIndentById', { masterIndentNo: indentNumber, status });
      }
      return result.data;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },
  async fetchChildIndent({ commit }, { indentNo }) {
    try {
      commit('setChildLeg', []);
      commit('setChildLegLoadingStatus', true);
      const result = await getChildIndents(indentNo);
      if (result && result.data) {
        commit('setChildLeg', result.data);
      }
      return result.data;
    } catch (error) {
      return HandleResponses.Status(error);
    } finally {
      commit('setChildLegLoadingStatus', false);
    }
  },
  async search({ commit }, { params }) {
    if (params.page === 1) {
      commit('setLoadingStatus', true);
    } else {
      commit('paginationCheck', true);
    }
    try {
      const result = await searchMasterIndent({ params });
      if (result && result.data) {
        commit('setAll', result.data);
      }
      return result.data;
    } catch (error) {
      return HandleResponses.Status(error);
    } finally {
      commit('paginationCheck', false);
      commit('setLoadingStatus', false);
    }
  },
  async getAttachments() {
    try {
      const result = await fetchAttachments();
      return result.data;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },
  async cancel({ commit }, { indentNo, cancelComment }) {
    try {
      commit('setCancelLoading', true);
      const result = await cancelMasterIndent(indentNo, cancelComment);
      if (result && result.data && result.data.length) {
        commit('setCancelState', { indentNo, data: result.data });
      }
      return result.data;
    } catch (error) {
      return HandleResponses.Status(error);
    } finally {
      commit('setCancelLoading', false);
    }
  },
  async deliverMasterIndent({ commit }, { indentNo, date }) {
    try {
      const result = await deliverMasterIndent(indentNo, date);
      if (result && !result.errors) {
        commit('setDeliverState', { indentNo });
      }
      return result;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },
  async masterDetails(_, { masterOrderNo }) {
    try {
      const result = await axios.get(`/exim/indent/${masterOrderNo}`);
      return result && result.data;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },
  async roadIndentDetails(_, { orderNo }) {
    try {
      const result = await axios.get(`/order/${orderNo}`);
      return result && result.data;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },
  async oceanIndentDetails(_, { orderNo }) {
    try {
      const result = await axios.get(`/generic-indent/${orderNo}`);
      return result && result.data;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },
  async roadLegsWithLr(_, orderNo) {
    try {
      const result = await axios.get(`exim/indent/${orderNo}/roadLegWithLr`);
      return result && result.data;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },
  async updateOceanDetails({ commit }, { indentNo, data }) {
    try {
      const result = await axios.put(`exim/indent/${indentNo}/update_fields`, {
        data,
      });
      const updateData = {
        vessel_date: data.fields.vessel_sailing_date,
        indent_number: indentNo,
      };
      commit('updateMasterIndentFields', updateData);
      return result;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },
  async updateMasterIndentAttachments({ commit }, { indentNo, payload }) {
    try {
      const result = await axios.put(`exim/indent/${indentNo}/attachments`, {
        data: payload,
      });
      commit('updateMasterIndentsData', result.data);
      return result;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },
  async removeAttachment(_, data) {
    try {
      const result = await axios.delete(`/attachment/${data.attachment_id}`);
      return result;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },
  async updateMaterials(_, data) {
    try {
      const result = await axios.put(`exim/indent/${data.masterIndentNumber}/materials`, { data: data.payload });
      return result;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },
  async fetchDropDownInput(_, { url, params }) {
    try {
      const result = await axios.get(`${url}`, { params });
      return result && result.data;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },
  async getMasterCustomFields(_, { type }) {
    try {
      const result = await fetchMasterCustomFields(type);
      return result.data;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },
  async genericIndentApproval({ commit },{ indentNo, values }) {
    try {
      const result = await updateApproval(indentNo, values);
      commit('updateMasterIndentsData', result.data);
      return result.data;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  }
};

export default {
  state,
  getters,
  actions,
  mutations,
};
